import { DataRow, useData } from '@hooks/useData';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { syncWait } from '@utils/Functions';
import { AxiosResponse } from 'axios';
import React from 'react';

export enum EnumFileUploadStatus {
    Awaitting = 1,
    Uploading = 2,
    Success = 3,
    Error = 4
}

export interface IFile {
    id: number
    hashName: string
    arquivo: File
    uploadStatus: EnumFileUploadStatus
    error?: string
}

export interface IUploadEventHandlerParams {
    file: DataRow<IFile>
    updateFileUploadStatus: (file: IFile, uploadStatus: EnumFileUploadStatus, error?: string | undefined) => void     
    setUploadedList: (value: React.SetStateAction<string[]>) => void
}

export type UploadEventHandler = (params: IUploadEventHandlerParams) => void;
export type AditionalParamsFunction<TAditionalParams = any> = (file: IFile) => TAditionalParams | undefined;
export type UploadFunction<TAditionalParams = any, TResult = never> = (arquivo: File, aditionalParams?: TAditionalParams) => Promise<AxiosResponse<TResult>>;

export type FileUploadParams<TAditionalParams = any, TResult = never> = {
    uploadFunction?: UploadFunction<TAditionalParams, TResult>
    onCollectAditionalParams?: AditionalParamsFunction<TAditionalParams>
    onUpload?: UploadEventHandler
}

export function useFileUpload<TAditionalParams = any, TResult = never>({ uploadFunction, onUpload, onCollectAditionalParams }: FileUploadParams<TAditionalParams, TResult>) {
    const loading = useLoading();
    const message = useMessage();
    
    const [hasAwaitting, setHasAwaitting] = React.useState(false);
    
    const [uploadedList, setUploadedList] = React.useState<string[]>([]);
    const data = useData<IFile>();
    const updateFileUploadStatus = React.useCallback((file: IFile, uploadStatus: EnumFileUploadStatus, error?: string) => {
        data.updateRow(file, {
            ...file,
            uploadStatus,
            error
        });
    }, [data]);

    const getFirstAwaittingFile = React.useCallback(() => {
        return data.items.sort((a, b) => a.id > b.id ? 1 : -1).find(x => x.uploadStatus === EnumFileUploadStatus.Awaitting);
    }, [data.items]);

    const _handleUpload = React.useCallback((params: IUploadEventHandlerParams) => {
        const { file, updateFileUploadStatus, setUploadedList } = params;

        updateFileUploadStatus(file, EnumFileUploadStatus.Uploading);

        const aditionalParams = onCollectAditionalParams !== undefined ? onCollectAditionalParams(file) : undefined;
        
        uploadFunction!(file.arquivo, aditionalParams)
            .then(() => {
                updateFileUploadStatus(file, EnumFileUploadStatus.Success);
                setUploadedList(s => [...s, file.hashName]);
            })
            .catch(e => {
                const errorMessage = message.getMessage(e);

                updateFileUploadStatus(file, EnumFileUploadStatus.Error, errorMessage);
                setUploadedList(s => [...s, file.hashName]);
            })
    }, [onCollectAditionalParams, uploadFunction, message]);

    const doUpload = React.useCallback(() => {
        const file = getFirstAwaittingFile();
        
        if (file !== undefined) {
            const funcParams = {
                file,
                updateFileUploadStatus,
                setUploadedList
            }

            if (uploadFunction !== undefined) {
                _handleUpload(funcParams);
            }
            else if (onUpload !== undefined) {
                onUpload(funcParams);
            }
            else {
                throw new Error ("Either uploadFunction or onUpload must be defined");
            }
        }

        return file !== undefined;

    }, [_handleUpload, getFirstAwaittingFile, onUpload, updateFileUploadStatus, uploadFunction]);

    const handleConfirm = React.useCallback(async () => {
        if (!loading.isLoading) {
            if (!data.isEmpty) {
                loading.start();
                doUpload()
           }
            else {
                message.warning("Não há arquivos para importar");
            }
        }
    }, [data.isEmpty, doUpload, loading, message]);

    const handleFilesSelected = React.useCallback((files: FileList) => {
        if (files.length > 0) {
            const m = files.length;

            const _files: IFile[] = [...data.items];

            for (let i = 0; i < m; i++) {
                const arquivo = files[i];
                const hashName = `${arquivo.name}-${arquivo.size}-${arquivo.lastModified}`;
                const existingFile = _files.find(x => x.hashName === hashName);
                const existingFileIndex = _files.findIndex(x => x.hashName === hashName);

                if (existingFileIndex !== -1 && existingFile !== undefined) {
                    _files[existingFileIndex] = {
                        ...existingFile,
                        uploadStatus: EnumFileUploadStatus.Awaitting,
                        error: undefined
                    }
                }
                else {
                    syncWait(10);
                    const id = new Date().getTime();
                    _files.push({
                        id,
                        hashName,
                        arquivo,
                        uploadStatus: EnumFileUploadStatus.Awaitting,
                    })
                }
            }

            data.updateAll(_files);

        }
    }, [data]);

    React.useEffect(() => {
        if (uploadedList.length > 0) {
            if (!doUpload()) {
                loading.stop();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedList]);

    React.useEffect(() => {
        setHasAwaitting(data.items.filter(x => x.uploadStatus === EnumFileUploadStatus.Awaitting).length > 0);
    }, [data.items]);

    const clear = React.useCallback(() => {
        data.clear();
        setUploadedList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return React.useMemo(() => ({
        loading,
        hasAwaitting,
        clear,
        isLoading: loading.isLoading,
        handleFilesSelected,
        data,
        handleConfirm
    }), [clear, data, handleConfirm, handleFilesSelected, hasAwaitting, loading]);

}